import React, { useState } from "react";
import styles from "../Assessment.module.scss";
import { combineClasses } from "@/lib/utils";
import { syncLanguagesAPI } from "@/services/api/ProgrammingLanguage";
import toast from "react-hot-toast";
import Table from "@/components/Table/Table";
import Pagination from "@/components/Table/Pagination";
import SearchBox from "@/components/SearchBox/SearchBox";
import EditIcon from "@/components/SVGComponents/EditIcon";
import Eye from "@/components/SVGComponents/Eye";
import TabPanel from "@/components/TabPanel/TabPanel";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import { updateAssessmentAPI } from "@/services/api/assessment";
import InlineManagerDropdown from "./InlineManagerDropdown";

interface AssessmentData {
  id: number;
  name: string;
  challengeCount: number;
  questionCount: number;
  freeformCount: number;
  invitedCount: number;
  assessedCount: number;
  qualifiedCount: number;
  createdByName: string;
  createdAt: string;
  lastActivity?: string;
  candidates?: any[]; // For detail table
}

interface AssessmentListProps {
  assessments: AssessmentData[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (val: string) => void;
  activeTab: string;
  onSelect: (item: AssessmentData) => void;
  onEdit?: (item: AssessmentData) => void;
  onCreate: () => void;
  page: number;
  setPage: (val: number) => void;
  totalRecords: number;
  perPage: number;
  onRefresh?: () => void;
  tabs: string[];
}

const AssessmentList: React.FC<AssessmentListProps> = ({
  assessments,
  loading,
  error,
  search,
  setSearch,
  activeTab,
  onSelect,
  onEdit,
  onCreate,
  page,
  setPage,
  totalRecords,
  perPage,
  onRefresh,
  tabs,
}) => {
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await syncLanguagesAPI();
      toast.success("Languages synced successfully from Judge0!");
    } catch (err) {
      toast.error("Failed to sync languages");
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateManager = async (assessmentId: number, managedById: number | null, email: string) => {
    try {
      const payload: any = {
        email: email || null,
        managedBy: managedById || null
      };
      await updateAssessmentAPI(assessmentId, payload);
      toast.success("Manager updated successfully");
      if (onRefresh) {
        onRefresh();
      }
    } catch (err: any) {
      // toast.error(err?.response?.data?.message || "Failed to update manager");
    }
  };

  const columns = [
    {
      name: "Name",
      selector: (row: any) => row.name,
      cell: (row: any) => (
        <span
          className={styles.assessmentName}
          onClick={() => {
            if (activeTab === "CLOSED") {
              toast.error("You can't send invitation as closed. If you want, then open assessment from setting.");
            } else {
              onSelect(row);
            }
          }}
          style={{ cursor: 'pointer', color: 'rgb(var(--primary-color))', fontWeight: '500' }}
        >
          {row.name}
        </span>
      ),
      sortable: true,
      width: '170px',
    },
    {
      name: "Invited",
      selector: (row: any) => row.invitedCount,
      sortable: true,
      width: '120px',
    },
    {
      name: "Assessed",
      selector: (row: any) => row.assessedCount,
      sortable: true,
      width: '120px',
    },
    {
      name: "Qualified",
      selector: (row: any) => row.qualifiedCount,
      sortable: true,
      width: '120px',
    },
    {
      name: "Challenges",
      selector: (row: any) => row.challengeCount,
      sortable: true,
      width: '120px',
    },
    {
      name: "Questions",
      selector: (row: any) => row.questionCount,
      sortable: true,
      width: '120px',
    },
    {
      name: "Free-form",
      selector: (row: any) => row.freeformCount,
      sortable: true,
      width: '120px',
    },
    {
      name: "Created",
      selector: (row: any) => row.createdAt,
      sortable: true,
      width: '140px',
    },
    // {
    //   name: "Last activity",
    //   selector: (row: any) => row.lastActivity || "N/A",
    //   sortable: true,
    //   width: '130px',
    // },
    {
      name: "Created by",
      selector: (row: any) => row.createdByName,
      sortable: true,
      width: '160px',
    },
    {
      name: "Managed by",
      selector: (row: any) => row?.managedBy?.name || "-",
      sortable: true,
      width: '140px',
    },
    {
      name: "Actions",
      cell: (row: any) => (
        <div className={styles.actionIcons} style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', width: '100%' }}>
          <button className={styles.actionBtn} title="Edit Assessment" onClick={() => onEdit ? onEdit(row) : onSelect(row)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <EditIcon />
          </button>
        </div>
      ),
      right: true,
      width: '100px',
    }
  ];

  return (
    <>
      <div className={styles.header}>
        <h1 className={styles.title}>Assessments</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          {/* <button className="btn-secondary" onClick={handleSync} disabled={syncing} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', fontWeight: '600', cursor: 'pointer' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={syncing ? "spin" : ""}><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
            {syncing ? "Syncing..." : "Sync Languages"}
          </button> */}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <TabPanel tabNames={tabs} />
        <button className="btn-primary" onClick={onCreate}>
          <PlusIcon />            New Assessment
        </button>
      </div>


      <div className={styles.contentCard}>
        <div className={styles.toolbar}>
          <SearchBox
            value={search}
            onChange={(val) => setSearch(val)}
            // placeholder="Search assessments..."
            width={240}
          />
          {/* <div className={styles.toolbarActions}>
            <button className={styles.iconBtn}>
               <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>
            </button>
            <div className={styles.viewToggle}>
               <button className={combineClasses(styles.iconBtn, styles.activeView)}>
                 <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
               </button>
               <button className={styles.iconBtn}>
                 <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/></svg>
               </button>
            </div>
          </div> */}
        </div>

        <div style={{ marginTop: '20px' }}>
          <Table
            columns={columns}
            data={Array.isArray(assessments) ? assessments : []}
            progressPending={loading}
          />
          <Pagination
            totalCount={totalRecords}
            pageSelected={page}
            setPageSelected={setPage}
            rowsPerPageValue={perPage}
          />
        </div>
      </div>
    </>
  );
};

export default AssessmentList;
